import * as THREE from 'three';
import { ObjectLoader } from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { PCDLoader } from 'three/examples/jsm/loaders/PCDLoader';
import { PDBLoader } from 'three/examples/jsm/loaders/PDBLoader';
import { USDLoader } from 'three/examples/jsm/loaders/USDLoader';
import { OBJParser } from './OBJParser';

/**
 * Utility class for loading various 3D model formats
 * Supports GLTF, GLB, OBJ, FBX, PCD, PDB, JSON, and USDZ formats
 */
export class MeshLoader {
  constructor() {
    // Initialize loaders
    this.gltfLoader = new GLTFLoader();
    this.objParser = new OBJParser();
    this.fbxLoader = new FBXLoader();
    this.pcdLoader = new PCDLoader();
    this.pdbLoader = new PDBLoader();
    this.objectLoader = new ObjectLoader();
    this.usdLoader = new USDLoader();
    this.mtlLoader = new MTLLoader();

    // Setup DRACO decoder for GLTF
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/static/libs/draco/gltf/');
    this.gltfLoader.setDRACOLoader(dracoLoader);
  }

  /**
   * Load a 3D model based on file extension
   * @param {string} url - URL to the model file
   * @param {string} modelType - File extension (gltf, glb, obj, fbx, etc.)
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Object3D>}
   */
  async load(url, modelType, onProgress) {
    if (!url) {
      console.warn('No URL provided for model loading');
      return;
    }

    const type = modelType.toLowerCase();

    switch (type) {
      case 'gltf':
      case 'glb':
        return this.loadGLTF(url, onProgress);
      case 'obj':
        return this.loadOBJ(url, onProgress);
      case 'fbx':
        return this.loadFBX(url, onProgress);
      case 'pcd':
        return this.loadPCD(url, onProgress);
      case 'pdb':
        return this.loadPDB(url, onProgress);
      case 'json':
        return this.loadObject(url, onProgress);
      case 'usdz':
        return this.loadUSD(url, onProgress);
      default:
        throw new Error(`Unsupported model type: ${modelType}`);
    }
  }

  /**
   * Load GLTF/GLB model
   * @param {string} url - URL to the GLTF/GLB file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Group>}
   */
  async loadGLTF(url, onProgress) {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        url,
        (gltf) => {
          gltf.scene.traverse((child) => {
            this.processChild(child);
          });
          resolve(gltf.scene);
        },
        onProgress,
        (error) => reject(error)
      );
    });
  }

  /**
   * Load FBX model
   * @param {string} url - URL to the FBX file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Group>}
   */
  async loadFBX(url, onProgress) {
    return new Promise((resolve, reject) => {
      this.fbxLoader.load(
        url,
        (object) => {
          object.traverse((child) => {
            if (child.isMesh) {
              // Handle materials with transparency
              if (child.material) {
                const materials = Array.isArray(child.material) ? child.material : [child.material];

                materials.forEach((material) => {
                  if (material.opacity < 1) {
                    material.alphaTest = 0.1;
                    material.needsUpdate = true;
                  }
                });
              }
              this.processChild(child);
            }
          });

          object.scale.set(0.0005, 0.0005, 0.0005);
          resolve(object);
        },
        onProgress,
        (error) => reject(error)
      );
    });
  }

  /**
   * Load OBJ model - handles large files by chunking geometry
   * @param {string} url - URL to the OBJ file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Group>}
   */
  async loadOBJ(url, onProgress) {
    return this.objParser.load(url, onProgress);
  }

  /**
   * Load PCD (Point Cloud Data) model
   * @param {string} url - URL to the PCD file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Points>}
   */
  async loadPCD(url, onProgress) {
    return new Promise((resolve, reject) => {
      this.pcdLoader.load(
        url,
        (points) => {
          points.rotation.set(-Math.PI / 2, 0, 0);
          points.castShadow = true;
          points.receiveShadow = true;
          resolve(points);
        },
        onProgress,
        (error) => reject(error)
      );
    });
  }

  /**
   * Load PDB (Protein Data Bank) model
   * @param {string} url - URL to the PDB file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Mesh>}
   */
  async loadPDB(url, onProgress) {
    return new Promise((resolve, reject) => {
      this.pdbLoader.load(
        url,
        (geometry, geometryAtoms, geometryBonds) => {
          const material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            specular: 0x111111,
            shininess: 30,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.rotation.set(-Math.PI / 2, 0, 0);
          this.processChild(mesh);
          resolve(mesh);
        },
        onProgress,
        (error) => reject(error)
      );
    });
  }

  /**
   * Load THREE.js JSON object
   * @param {string} url - URL to the JSON file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Object3D>}
   */
  async loadObject(url, onProgress) {
    return new Promise((resolve, reject) => {
      this.objectLoader.load(
        url,
        (object) => {
          object.rotation.set(-Math.PI / 2, 0, 0);
          object.scale.set(0.0005, 0.0005, 0.0005);
          object.traverse((child) => {
            this.processChild(child);
          });
          resolve(object);
        },
        onProgress,
        (error) => reject(error)
      );
    });
  }

  /**
   * Load USDZ model
   * @param {string} url - URL to the USDZ file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Group>}
   */
  async loadUSD(url, onProgress) {
    return new Promise((resolve, reject) => {
      this.usdLoader.load(
        url,
        (object) => {
          object.rotation.set(-Math.PI / 2, 0, 0);
          object.traverse((child) => {
            this.processChild(child);
          });
          resolve(object);
        },
        onProgress,
        (error) => reject(error)
      );
    });
  }

  /**
   * Process child mesh to ensure proper rendering
   * @param {THREE.Object3D} child - Child object to process
   */
  processChild(child) {
    if (child.isMesh) {
      const geometry = child.geometry;
      if (geometry && !geometry.getAttribute('normal')) {
        geometry.computeVertexNormals();
      }
      child.castShadow = true;
      child.receiveShadow = true;
    }
  }

  /**
   * Dispose loader resources (DRACO decoder, etc.)
   * Call during scene cleanup to release memory.
   */
  dispose() {
    const dracoLoader = this.gltfLoader.dracoLoader;
    if (dracoLoader) {
      dracoLoader.dispose();
    }
  }

  /**
   * Get file extension from URL
   * @param {string} url - URL to extract extension from
   * @returns {string} - File extension (without dot)
   */
  static getExtensionFromUrl(url) {
    const match = url.match(/\.([^./?#]+)(?:[?#]|$)/);
    return match ? match[1].toLowerCase() : '';
  }
}

// Export singleton instance for convenience
export const meshLoader = new MeshLoader();
